// src/App.tsx

import React, { useState, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { COMPANY_COLORS } from './constants/theme.constants';
import { useLoanData } from './hooks/useLoanData';
import { Header, FilterType } from './components/common/Header';
import { LoanTable } from './components/dashboard/LoanTable';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { queryClient } from './lib/queryClient';
import { filterLoans } from './utils/loanFilters';
import { LoanStatus } from './types/loan.types';
import { apiService } from './services/api';
import { TokenManager } from './utils/tokenManager';

import { useAuth } from './services/authService';

const AppContent: React.FC = () => {
  const { data: authState } = useAuth();
  const isAdmin = authState?.authority?.roles?.includes("DASHBOARD_ADMIN");
  const userCountry = authState?.user?.country || 'NG';

  const [selectedCountry, setSelectedCountry] = useState<string>(isAdmin ? 'ALL' : userCountry);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LoanStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { loans, stats, pagination, isLoading, error, refetch } = useLoanData(selectedCountry, currentPage, pageSize);

  // ── Proactive token refresh ────────────────────────────────────────────────
  // Runs every 5 minutes. If the access token has < 10 minutes left it silently
  // refreshes so the user is never kicked out mid-session due to expiry.
  useEffect(() => {
    const refresh = async () => {
      const remaining = TokenManager.getRemainingTime(); // seconds
      const hasRefresh = !!TokenManager.getRefreshToken();
      // Refresh if: token expires in < 10 min OR is already expired but we have a refresh token
      if (hasRefresh && (remaining === 0 || remaining < 10 * 60)) {
        console.log('⏰ Proactive refresh — token expires in', remaining, 's');
        await apiService.tryRefreshToken();
      }
    };
    refresh(); // Check immediately on mount
    const interval = setInterval(refresh, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, []);



  // Reset to first page when country changes
  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setCurrentPage(1);
    setActiveFilter('all');
    setSearch('');
    setStatusFilter('all');
  };


  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${COMPANY_COLORS.pageBg}80, ${COMPANY_COLORS.surface})`,
        }}
      >
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">
            {error instanceof Error && error.message.includes('authentication')
              ? 'Your session has expired. Please login again.'
              : 'Unable to load loan data. Please check your connection.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Filter loans based on all filters
  const filteredLoans = filterLoans(loans, activeFilter, search, statusFilter);

  return (
    <div
      className="min-h-screen py-0"
      style={{
        background: `linear-gradient(135deg, ${COMPANY_COLORS.pageBg}80, ${COMPANY_COLORS.surface})`,
      }}
    >
      <Header
        stats={stats}
        selectedCountry={selectedCountry}
        activeFilter={activeFilter}
        onCountryChange={handleCountryChange}
        onFilterChange={handleFilterChange}
      />

      <div className="max-w-full px-4 mx-auto">
        <LoanTable
          loans={filteredLoans}
          country={selectedCountry}
          isLoading={isLoading}
          activeFilter={activeFilter}
          totalLoans={pagination.total}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          isAdmin={isAdmin}
        />
      </div>

    </div>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ProtectedRoute>
        <AppContent />
      </ProtectedRoute>
    </QueryClientProvider>
  );
};

export default App;